export interface GoogleFont {
  family: string;
  category: 'sans-serif' | 'serif' | 'monospace' | 'display';
  weights: number[];
}

export const WEIGHT_LABELS: Record<number, string> = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
};

/** Normalize legacy string weights ('bold'/'normal') to numeric values. */
export const normalizeWeight = (w: 'normal' | 'bold' | number | undefined): number =>
  w === 'bold' ? 700 : w === 'normal' ? 400 : w ?? 400;

export const GOOGLE_FONTS: GoogleFont[] = [
  // Sans-serif (variable fonts with full weight range unless noted)
  { family: 'Inter', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Roboto', category: 'sans-serif', weights: [100, 300, 400, 500, 700, 900] },
  { family: 'Open Sans', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
  { family: 'Poppins', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Montserrat', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Lato', category: 'sans-serif', weights: [100, 300, 400, 700, 900] },
  { family: 'Raleway', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Nunito', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'DM Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Work Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Space Grotesk', category: 'sans-serif', weights: [300, 400, 500, 600, 700] },
  { family: 'Fira Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Karla', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: 'Libre Franklin', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Archivo', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Oswald', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700] },
  { family: 'Cairo', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Noto Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Noto Sans Arabic', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'PT Sans', category: 'sans-serif', weights: [400, 700] },
  // Serif
  { family: 'Merriweather', category: 'serif', weights: [300, 400, 700, 900] },
  { family: 'Playfair Display', category: 'serif', weights: [400, 500, 600, 700, 800, 900] },
  { family: 'Lora', category: 'serif', weights: [400, 500, 600, 700] },
  { family: 'Libre Baskerville', category: 'serif', weights: [400, 700] },
  { family: 'Source Serif 4', category: 'serif', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: 'Cormorant', category: 'serif', weights: [300, 400, 500, 600, 700] },
  { family: 'Alegreya', category: 'serif', weights: [400, 500, 600, 700, 800, 900] },
  { family: 'Fraunces', category: 'serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  // Monospace
  { family: 'Space Mono', category: 'monospace', weights: [400, 700] },
  // Display
  { family: 'Bebas Neue', category: 'display', weights: [400] },
];

/** Build the Google Fonts weight axis string for a font (e.g. "wght@100;300;400;700"). */
const buildWeightAxis = (weights: number[]): string => `wght@${weights.join(';')}`;

const loadedFonts = new Set<string>();
const loadedWeights = new Set<string>();
const linkLoadPromises = new Map<string, Promise<void>>();
let allFontsLinkInjected = false;

/**
 * Inject the Google Fonts CSS link for a single family and wait for the stylesheet
 * to load so that @font-face rules are parsed before document.fonts.load() is called.
 */
const injectFontLink = (family: string): Promise<void> => {
  const linkId = `gfont-${family.replace(/ /g, '-')}`;

  const existing = linkLoadPromises.get(linkId);
  if (existing) return existing;

  const font = GOOGLE_FONTS.find((f) => f.family === family);
  const weightAxis = font ? buildWeightAxis(font.weights) : 'wght@400;700';

  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:${weightAxis}&display=swap`;

  const loadPromise = new Promise<void>((resolve) => {
    link.onload = () => resolve();
    link.onerror = () => {
      console.warn(`[fonts] Failed to load stylesheet for "${family}"`);
      resolve();
    };
    setTimeout(resolve, 5000);
  });

  linkLoadPromises.set(linkId, loadPromise);
  document.head.appendChild(link);
  return loadPromise;
};

/**
 * Load all 30 builder fonts in a single batched Google Fonts API request.
 * Call once on builder page mount.
 */
export const loadAllBuilderFonts = async (): Promise<void> => {
  if (allFontsLinkInjected) return;
  allFontsLinkInjected = true;

  const families = GOOGLE_FONTS.map(
    (f) => `family=${f.family.replace(/ /g, '+')}:${buildWeightAxis(f.weights)}`
  ).join('&');

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;

  const cssLoaded = new Promise<void>((resolve) => {
    link.onload = () => resolve();
    link.onerror = () => resolve();
    setTimeout(resolve, 10000);
  });

  document.head.appendChild(link);

  // Wait for stylesheet to be parsed so @font-face rules are available.
  // NOTE: Do NOT mark fonts as loaded here. With font-display:swap, the browser
  // only downloads font binaries when they're referenced in visible DOM content.
  // At builder mount time no canvas text exists yet, so document.fonts.ready
  // resolves immediately — marking weights as loaded would cause loadGoogleFont()
  // to skip the actual document.fonts.load() call, leaving text in fallback fonts.
  await cssLoaded;
};

/**
 * Load a single Google Font weight on demand and wait until it's usable by Fabric.js.
 */
export const loadGoogleFont = async (family: string, weight: number = 400): Promise<void> => {
  const key = `${family}:${weight}`;
  if (loadedWeights.has(key)) return;

  await injectFontLink(family);

  try {
    await document.fonts.load(`${weight} 16px "${family}"`);
    loadedWeights.add(key);
    loadedFonts.add(family);
  } catch {
    console.warn(`[fonts] Failed to load "${family}" weight ${weight}, Fabric will use fallback`);
  }
};

/**
 * Load all available weights for a font family at once.
 * Use when the user selects a new font family so every weight is immediately available.
 */
export const loadAllWeights = async (family: string): Promise<void> => {
  const font = GOOGLE_FONTS.find((f) => f.family === family);
  if (!font) return loadGoogleFont(family);

  await injectFontLink(family);

  await Promise.all(
    font.weights.map((w) => {
      const key = `${family}:${w}`;
      if (loadedWeights.has(key)) return Promise.resolve();
      return document.fonts.load(`${w} 16px "${family}"`).then(() => {
        loadedWeights.add(key);
      }).catch(() => {
        // Individual weight failed — non-fatal
      });
    }),
  );
  loadedFonts.add(family);
};

/**
 * Check whether a specific font weight has been loaded and is ready for canvas use.
 */
export const isFontLoaded = (family: string, weight?: number): boolean => {
  if (weight !== undefined) return loadedWeights.has(`${family}:${weight}`);
  return loadedFonts.has(family);
};
